import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, PLATFORM_ID, SimpleChanges } from '@angular/core';
import { CookiesService } from '@app/core/services/cookies.service';
import * as fromRoot from '@app/reducers';
import { pageAnimation } from '@app/shared/animations/page-animations';
import { Background } from '@app/shared/models/background';
import { Campaign } from '@app/shared/models/campaign';
import { Device } from '@app/shared/models/device';
import { Preview } from '@app/shared/models/preview';
import { Team } from '@app/shared/models/team';
import { TransferFile } from '@app/shared/models/transferFile';
import { select, Store } from '@ngrx/store';
import { Options, SourceInfo } from 'plyr';
import { Observable } from 'rxjs';
import { Constant } from 'src/constant';
import { ViewerActions } from '../actions';
import * as fromViewer from '../reducers/viewer.reducer';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-cover',
  templateUrl: './cover.component.html',
  styleUrls: ['./cover.component.scss'],
  animations: [
    pageAnimation,
  ]
})
export class CoverComponent implements OnInit, OnChanges {

  @Input() preview: Preview;
  @Input() device: Device;
  @Input() files: TransferFile[];
  @Input() countdownDisplayed: boolean;
  @Input() isDownloading: boolean;
  @Input() countryCode: string;

  @Output() triggerCoverCLick: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() videoReadyToPlay: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() backToPreview: EventEmitter<boolean> = new EventEmitter();

  menuDisplayed$: Observable<boolean>;
  selectedTeam$: Observable<Team>;
  hasToken$: Observable<boolean>;
  isPremium$: Observable<boolean>;
  selectedLanguage$: Observable<string>;
  campaign$: Observable<Campaign>;

  showSeeFiles = false;
  seeFilesLineResize = false;
  currentTooltipState: 'in' | 'out' = 'in';
  showVideo = false;

  playerSource: SourceInfo;
  playerOptions: Options;

  constructor(
    private readonly store$: Store<fromRoot.State & fromViewer.State>,
    private cookiesService: CookiesService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    this.menuDisplayed$ = this.store$.pipe(select(fromViewer.getMenuDisplayed));
    this.selectedTeam$ = this.store$.pipe(select(fromRoot.getActiveTeam));
    this.hasToken$ = this.store$.pipe(select(fromRoot.getHasToken));
    this.isPremium$ = this.store$.pipe(select(fromRoot.getIsPremium));
    this.selectedLanguage$ = this.store$.pipe(select(fromRoot.getSelectedLanguage));
    this.campaign$ = this.store$.pipe(select(fromRoot.getSelectedCampaignFreemium));
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.startIntervalTooltipInfos();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes.preview && isPlatformBrowser(this.platformId)) {
      this.startSeeFilesAnimation();
    }

    if (!!changes?.preview?.currentValue) {
      this.setupPlayer();
    }
  }

  get isPreviewDisable(): boolean {
    return this.preview?.transfer?.preview === Constant.previewStatus.None;
  }

  get isTransferAvailable() {
    return this.preview?.transfer?.status === Constant.transferStatus.Uploaded;
  }

  onToolTipClick(): void {
    if (this.preview?.transfer?.status === Constant.transferStatus.Uploaded) {
      if (this.preview?.transfer?.preview === Constant.previewStatus.None) {
        this.store$.dispatch(ViewerActions.RequestDownloadUrls({}));
        this.store$.dispatch(ViewerActions.DisplayDownloadSection());
      } else if (this.preview?.transfer?.preview === Constant.previewStatus.Full) {
        if (this.device.isDesktop) {
          this.store$.dispatch(ViewerActions.RequestDownloadUrls({}));
          this.store$.dispatch(ViewerActions.DisplayDownloadSection());
        } else {
          this.triggerCoverCLick.emit();
        }
      }
    }
  }

  showPreview(): void {
    this.triggerCoverCLick.emit();
  }

  onToggleMenu(event) {
    this.store$.dispatch(ViewerActions.ToggleMenu({ menuDisplayed: event.toggled }));
    event.event?.stopPropagation();
    return false;
  }

  private startIntervalTooltipInfos(): void {
    setInterval(() => {
      this.currentTooltipState = this.currentTooltipState === 'in' ? 'out' : 'in';
    }, 3000);
  }

  /* Start animation "view files" on the bottom of the cover */
  private startSeeFilesAnimation() {
    setTimeout(() => {
      this.seeFilesLineResize = true;
    }, 800);
    setTimeout(() => {
      this.showSeeFiles = true;
    }, 1200);
  }

  isVideoBgReadyToPlay(event): void {
    if (isPlatformBrowser(this.platformId)) {
      this.videoReadyToPlay.emit(event);
      setTimeout(() => {
        this.showVideo = true;
      }, 500);
    }
  }

  // Player
  setupPlayer(): void {
    if (this.preview?.transfer?.customization?.background?.type === Constant.providerPlayerTypes.vimeo || this.preview?.transfer?.customization?.background?.type === Constant.providerPlayerTypes.youtube) {
      this.playerSource = this.buildPlayerSource(this.preview.transfer.customization.background);
      this.playerOptions = this.buildPlayerOptions(this.preview.transfer.customization.background);
    }
  }

  buildPlayerSource(background: Background): SourceInfo {
    const source: SourceInfo = {
      sources: [{
        src: background.sourceId,
        provider: background.type.toLowerCase() as Plyr.Provider,
      }],
      type: 'video' as Plyr.MediaType,
    }
    return source;
  }

  buildPlayerOptions(item: any): Options {
    const options: Options = {
      autoplay: true,
      clickToPlay: false,
      controls: [],
      volume: 0,
      storage: { enabled: false },
      loop: { active: true },
    };

    if (item.type === Constant.providerPlayerTypes.youtube) {
      options.youtube = { noCookie: !this.cookiesService.getCookie('youtubeConsent'), rel: 0, controls: 0, autohide: 1, showinfo: 0, iv_load_policy: 3, modestbranding: 1 };
    }

    if (item.type === Constant.providerPlayerTypes.vimeo) {
      options.vimeo = { dnt: !this.cookiesService.getCookie('vimeoConsent') ? 0 : 1, byline: 0, title: 0, background: 1, loop: 1, autoplay: 1, muted: 1 };
    }
    return options;
  }
}

